import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { env } from '@/config/env';

export const MODULE_IDS_TO_SKIP = new Set([
  'all-sessions',
  'previous-sessions',
  'previous-batch-sessions',
]);

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function reverseArray<T>(array: T[]): T[] {
  return [...array].reverse();
}

export const downloadPdf = async (url: string, filename: string) => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.pathname = parsedUrl.pathname
      .split('/')
      .map(segment => encodeURIComponent(decodeURIComponent(segment)))
      .join('/');
    const encodedUrl = parsedUrl.toString();
    const response = await fetch(encodedUrl, {
      credentials: 'include',
    });
    const blob = await response.blob();
    const downloadUrl = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(downloadUrl);
  } catch {
    window.open(url, '_blank');
  }
};

export const noop = () => {
  // do nothing
};

export const getCleanRoomName = (roomName: string) => {
  if (!roomName) {
    return 'No title available';
  }
  let title = roomName;

  if (title.includes(':')) {
    const splitArrays = title.split(':');
    title = splitArrays.at(-1) ?? title;
  }
  return title.trim();
};

const getRoomNameSorter = (roomName: string) => {
  if (!roomName) {
    return '';
  }
  if (!env.IS_OFFLINE) {
    return roomName;
  }
  let title = roomName;
  if (title.includes(':')) {
    const [_title] = title.split(':');
    title = _title;
  }
  return title.trim();
};

const parseRoomName = (name: string) => {
  const regex = /^(\d{1,3})C\.M(\d{1,3})\.S(\d{1,3})$/;
  const match = regex.exec(name);
  if (!match) return { c: 0, m: 0, s: 0 };
  return {
    c: Number.parseInt(match[1], 10),
    m: Number.parseInt(match[2], 10),
    s: Number.parseInt(match[3], 10),
  };
};

export const sortByRoomName = (a: string, b: string) => {
  const parsedA = parseRoomName(getRoomNameSorter(a));
  const parsedB = parseRoomName(getRoomNameSorter(b));

  if (parsedA.c !== parsedB.c) return parsedA.c - parsedB.c;
  if (parsedA.m !== parsedB.m) return parsedA.m - parsedB.m;
  return parsedA.s - parsedB.s;
};

export const getSessionLabel = (): {
  lowercase: string;
  uppercase: string;
  capitalize: string;
} => {
  const label = env.IS_OFFLINE ? 'lesson' : 'session';
  return {
    lowercase: label.toLowerCase(),
    uppercase: label.toUpperCase(),
    capitalize: label.charAt(0).toUpperCase() + label.slice(1),
  };
};

export const getNumberToOrdinalName = (number: number) => {
  if (number === 0) return 'Zero';
  if (!number) return '';
  if (number === 1) return 'First';
  if (number === 2) return 'Second';
  if (number === 3) return 'Third';
  if (number === 4) return 'Fourth';
  if (number === 5) return 'Fifth';
  return `${number}th`;
};

export const formatSecondsToHHMMSS = (time: number) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const isPlainObject = (
  value: unknown
): value is Record<string, unknown> => {
  // Reject primitives and null
  if (typeof value !== 'object' || value === null) return false;

  // Use Object.prototype.toString for reliable type checking
  if (Object.prototype.toString.call(value) !== '[object Object]') return false;

  // Get the prototype of the value
  const proto = Object.getPrototypeOf(value) as object | null;

  // Objects created with Object.create(null) have no prototype
  if (proto === null) return true;

  // Check if the constructor is Object
  // Use hasOwnProperty safely to avoid issues with objects that don't have this method
  const hasConstructor = Object.prototype.hasOwnProperty.call(
    proto,
    'constructor'
  );

  if (!hasConstructor) return false;

  // Type assertion is safe here because we've verified the property exists
  const Ctor = (proto as { constructor: unknown }).constructor;

  // Verify constructor is a function and is the built-in Object constructor
  return typeof Ctor === 'function' && Ctor === Object;
};

type DeviceType = 'android' | 'iphone' | 'desktop';

export function getUserDevice(): DeviceType {
  if (typeof navigator === 'undefined') {
    // SSR safety (Next.js)
    return 'desktop';
  }
  const userAgent = navigator.userAgent.toLowerCase();
  if (/android/.test(userAgent)) {
    return 'android';
  }
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'iphone';
  }
  return 'desktop';
}
